class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = webvista.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // 监听第一个form， 每一个facet-filters-form元素只有一个form
    const facetForm = this.querySelector("form");
    facetForm.addEventListener("change", this.debouncedOnSubmit.bind(this));

    // 监听键盘ESCAPE事件，关闭展开
    this.addEventListener("keyup", webvista.onKeyUpEscape);
  }

  /**
   * 监听浏览器搜索历史变化事件
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener("popstate", onHistoryChange);
  }

  /**
   * 渲染页面
   * @param searchParams 查询参数，由筛选和排序构成
   * @param event
   * @param updateURLHash
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();

    const productGridContainer =
      document.getElementById("Product-Grid-Container");

    const contentContainer =
      productGridContainer?.querySelector(".content-list");

    if (contentContainer) {
      contentContainer.classList.add("loading");
    }

    sections.forEach((section) => {
      const url =
        `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  /**
   * 异步获取搜索结果
   * @param url
   * @param event
   */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`,
          );
        }

        return response.text();
      })
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];

        FacetFiltersForm.renderFacets(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch(() => {
        webvista.popToast(
          window["accessibilityStrings"]["unknownError"],
          "error",
        );
      });
  }

  /**
   * 缓存获取结果
   * @param filterDataUrl
   * @param event
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html =
      FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFacets(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  /**
   * 渲染产品网格
   * @param html
   */
  static renderProductGridContainer(html) {
    const targetProductContainer =
      document.getElementById("Product-Grid-Container");

    if (!targetProductContainer) return false;

    const parsedDocument =
      new DOMParser().parseFromString(html, "text/html");

    const sourceProductContainer =
      parsedDocument.getElementById("Product-Grid-Container");

    if (!sourceProductContainer) return false;

    targetProductContainer.innerHTML =
      sourceProductContainer.innerHTML;

    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(targetProductContainer);
    }

    webvista.initLazyImages();
    webvista.initTooltips();

    return true;
  }

  /**
   * 修改查询的产品总数
   * 分为【垂直布局】情况和【水平 + 抽屉】情况
   * @param html
   */
  static renderProductCount(html) {
    const countIds = [
      "ProductCountVertical",
      "Product-Count-Horizontal-And-Drawer",
    ];

    const htmlDocument =
      new DOMParser().parseFromString(html, "text/html");

    countIds.forEach((id) => {
      const source = htmlDocument.getElementById(id);
      const target = document.getElementById(id);

      if (source && target) {
        target.outerHTML = source.outerHTML;
      }
    });
  }

  /**
   * 渲染筛选和排序
   * @param html
   * @param event
   */
  static renderFacets(html, event) {
    const parsedHTML =
      new DOMParser().parseFromString(html, "text/html");

    const selectors = [
      "#Facet-Horizontal-Wrapper",
      "#Facet-Vertical-Wrapper",
      "#Facet-Drawer-Opener-Wrapper",
      "#Facet-Drawer-Wrapper",
    ];

    selectors.forEach((selector) => {
      const target = document.querySelector(selector);
      const source = parsedHTML.querySelector(selector);

      if (!target || !source) return;

      /*
       * Befindet sich das auslösende Element innerhalb dieses
       * Facet-Bereichs, wird der aktuell bediente Filter bewusst
       * nicht vollständig ersetzt.
       *
       * Dadurch bleibt ein geöffnetes Dropdown / Disclosure offen.
       */
      if (event && target.contains(event.target)) {
        const facetItem =
          event.target.closest(".facet-item");

        const matchesIndex = (element) => {
          if (!facetItem) return false;

          return (
            element.dataset.index ===
            facetItem.dataset.index
          );
        };

        /*
         * Aktive Filter / Chips aktualisieren.
         */
        const facetActiveSource =
          source.querySelector(".facets-active");

        const facetActiveTarget =
          target.querySelector(".facets-active");

        if (facetActiveSource && facetActiveTarget) {
          facetActiveTarget.innerHTML =
            facetActiveSource.innerHTML;
        }

        /*
         * Alle NICHT aktuell bedienten Filter vollständig
         * aus dem neuen Server-Markup ersetzen.
         */
        const facets =
          Array.from(source.querySelectorAll(".facet-item"));

        facets
          .filter((facet) => !matchesIndex(facet))
          .forEach((facet) => {
            if (!facet.hasAttribute("data-index")) return;

            const facetIndex =
              facet.getAttribute("data-index");

            const targetFacet =
              target.querySelector(
                `.facet-item[data-index="${facetIndex}"]`,
              );

            if (targetFacet) {
              targetFacet.outerHTML = facet.outerHTML;
            }
          });

        /*
         * Aktuell bedienten Filter ermitteln.
         */
        const currentFacet =
          facets.find((facet) => matchesIndex(facet));

        if (!currentFacet) return;

        const currentFacetIndex =
          currentFacet.getAttribute("data-index");

        const targetFacet =
          target.querySelector(
            `.facet-item[data-index="${currentFacetIndex}"]`,
          );

        if (!targetFacet) return;

        /*
         * FlauschPalast:
         *
         * Früher wurde hier lediglich ".facets-selected"
         * ersetzt.
         *
         * Das genügte für die Anzahl "(1)", "(2)" usw.,
         * aktualisierte aber NICHT:
         *
         * - aktive Farbswatches
         * - +1 / +2 / +X
         * - aktive Summary-Klassen
         * - weitere dynamische Summary-Inhalte
         *
         * Deshalb wird jetzt der INHALT des Summary aktualisiert,
         * während das vorhandene <details> und <summary> bestehen
         * bleiben.
         *
         * Dadurch bleibt der aktuell geöffnete Filter offen.
         */
        const targetSummary =
          targetFacet.querySelector(".facets-summary");

        const currentSummary =
          currentFacet.querySelector(".facets-summary");

        if (targetSummary && currentSummary) {
          /*
           * Aktuellen aria-expanded-Zustand sichern.
           * Dieser beschreibt den realen UI-Zustand im Browser.
           */
          const preservedAriaExpanded =
            targetSummary.getAttribute("aria-expanded");

          const hadAriaExpanded =
            targetSummary.hasAttribute("aria-expanded");

          /*
           * Gesamten dynamischen Summary-Inhalt übernehmen.
           *
           * Damit werden insbesondere:
           * Anzahl, Swatches, +X und Caret aktualisiert.
           */
          targetSummary.innerHTML =
            currentSummary.innerHTML;

          /*
           * Klassen ebenfalls synchronisieren.
           *
           * Wichtig für:
           * .fp-facet-is-active
           */
          targetSummary.className =
            currentSummary.className;

          /*
           * aria-label aus dem neuen Server-Markup übernehmen.
           */
          const sourceAriaLabel =
            currentSummary.getAttribute("aria-label");

          if (sourceAriaLabel !== null) {
            targetSummary.setAttribute(
              "aria-label",
              sourceAriaLabel,
            );
          } else {
            targetSummary.removeAttribute("aria-label");
          }

          /*
           * Den tatsächlichen geöffneten Zustand beibehalten.
           */
          if (hadAriaExpanded) {
            targetSummary.setAttribute(
              "aria-expanded",
              preservedAriaExpanded,
            );
          } else {
            targetSummary.removeAttribute("aria-expanded");
          }
        }
      } else {
        /*
         * Nicht vom Event betroffener Facet-Bereich:
         * komplett aus dem neuen Server-Markup aktualisieren.
         *
         * Dadurch bleiben Horizontal, Vertical und Drawer
         * gegenseitig synchron.
         */
        target.innerHTML = source.innerHTML;
      }
    });
  }

  /**
   * 更新浏览器的历史记录
   * 而不会触发页面的重新加载
   * @param searchParams 搜索参数
   */
  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  /**
   * 获取需要重新渲染的Sections
   * @returns {[{section: string}]}
   */
  static getSections() {
    const paginateContent =
      document.getElementById("Paginate-Content");

    if (!paginateContent) return [];

    return [
      {
        section: paginateContent.dataset.section,
      },
    ];
  }

  /**
   * 构造请求参数
   * @param form
   * @returns {string}
   */
  createSearchParams(form) {
    const formData = new FormData(form);

    return new URLSearchParams(formData).toString();
  }

  /**
   * 提交表单
   * @param searchParams
   * @param event
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * 监听筛选和排序处理函数
   * @param event
   */
  onSubmitHandler(event) {
    event.preventDefault();

    const form = this.querySelector("form");

    this.onSubmitForm(
      this.createSearchParams(form),
      event,
    );
  }

  /**
   * 已选选项，点击事件处理
   * @param event
   */
  onActiveFilterClick(event) {
    event.preventDefault();

    const url =
      event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );

    FacetFiltersForm.renderPage(url, null);
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

customElements.define(
  "facet-filters-form",
  FacetFiltersForm,
);

FacetFiltersForm.setListeners();


class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector("input.range-min");
    this.rangeMax = this.querySelector("input.range-max");
    this.inputMin = this.querySelector("input.input-min");
    this.inputMax = this.querySelector("input.input-max");

    if (!this.rangeMin || !this.rangeMax) return;

    // Debounce state
    this.debounceMs = 1000;
    this.emitTimer = null;

    this.boundOnRangeMinChange =
      this.onRangeMinChange.bind(this);

    this.boundOnRangeMaxChange =
      this.onRangeMaxChange.bind(this);

    this.boundOnInputMinChange =
      this.onInputMinChange.bind(this);

    this.boundOnInputMaxChange =
      this.onInputMaxChange.bind(this);

    this.boundStopEmit =
      this.stopEmit.bind(this);

    this.initBlockNativeChange();

    this.rangeMin.addEventListener(
      "input",
      this.boundOnRangeMinChange,
    );

    this.rangeMax.addEventListener(
      "input",
      this.boundOnRangeMaxChange,
    );

    if (this.inputMin) {
      this.inputMin.addEventListener(
        "input",
        this.boundStopEmit,
      );

      this.inputMin.addEventListener(
        "change",
        this.boundOnInputMinChange,
      );
    }

    if (this.inputMax) {
      this.inputMax.addEventListener(
        "input",
        this.boundStopEmit,
      );

      this.inputMax.addEventListener(
        "change",
        this.boundOnInputMaxChange,
      );
    }

    this.setProgressMin();
    this.setProgressMax();
  }

  disconnectedCallback() {
    if (
      this.rangeMin &&
      this.boundOnRangeMinChange
    ) {
      this.rangeMin.removeEventListener(
        "input",
        this.boundOnRangeMinChange,
      );
    }

    if (
      this.rangeMax &&
      this.boundOnRangeMaxChange
    ) {
      this.rangeMax.removeEventListener(
        "input",
        this.boundOnRangeMaxChange,
      );
    }

    if (
      this.inputMin &&
      this.boundOnInputMinChange
    ) {
      this.inputMin.removeEventListener(
        "change",
        this.boundOnInputMinChange,
      );
    }

    if (
      this.inputMax &&
      this.boundOnInputMaxChange
    ) {
      this.inputMax.removeEventListener(
        "change",
        this.boundOnInputMaxChange,
      );
    }

    if (this.boundStopEmit) {
      if (this.inputMin) {
        this.inputMin.removeEventListener(
          "input",
          this.boundStopEmit,
        );
      }

      if (this.inputMax) {
        this.inputMax.removeEventListener(
          "input",
          this.boundStopEmit,
        );
      }
    }

    this.stopEmit();
    this.removeBlockNativeChange();
  }

  /**
   * 统一阻止 input 的“原生 change 冒泡”（捕获阶段）
   */
  initBlockNativeChange() {
    this.boundBlockNativeChange =
      (event) => event.stopPropagation();

    [
      this.rangeMin,
      this.rangeMax,
      this.inputMin,
      this.inputMax,
    ]
      .filter(Boolean)
      .forEach((element) => {
        element.addEventListener(
          "change",
          this.boundBlockNativeChange,
        );
      });
  }

  /**
   * 解除捕获阶段的原生change拦截
   */
  removeBlockNativeChange() {
    [
      this.rangeMin,
      this.rangeMax,
      this.inputMin,
      this.inputMax,
    ]
      .filter(Boolean)
      .forEach((element) => {
        element.removeEventListener(
          "change",
          this.boundBlockNativeChange,
        );
      });
  }

  /**
   * 调度派发 change 事件
   */
  scheduleEmit() {
    this.stopEmit();

    this.emitTimer = setTimeout(() => {
      this.dispatchEvent(
        new Event("change", { bubbles: true }),
      );
    }, this.debounceMs);
  }

  /**
   * 范围最小值变化
   * @param {Event} event
   */
  onRangeMinChange(event) {
    const minVal =
      webvista.toNumber(this.rangeMin.value);

    const maxVal =
      webvista.toNumber(
        this.rangeMax.value,
        minVal,
      );

    if (minVal >= maxVal) {
      this.rangeMin.value =
        String(maxVal);
    }

    if (this.inputMin) {
      this.inputMin.value =
        this.rangeMin.value;
    }

    this.setProgressMin();
    this.scheduleEmit();
  }

  /**
   * 范围最大值变化
   * @param {Event} event
   */
  onRangeMaxChange(event) {
    const maxVal =
      webvista.toNumber(this.rangeMax.value);

    const minVal =
      webvista.toNumber(
        this.rangeMin.value,
        maxVal,
      );

    if (maxVal <= minVal) {
      this.rangeMax.value =
        String(minVal);
    }

    if (this.inputMax) {
      this.inputMax.value =
        this.rangeMax.value;
    }

    this.setProgressMax();
    this.scheduleEmit();
  }

  /**
   * 停止发送change事件
   */
  stopEmit() {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
  }

  /**
   * 最小输入变化
   * @param {Event} event
   */
  onInputMinChange(event) {
    let value =
      webvista.toNumber(event.currentTarget.value);

    const min =
      webvista.toNumber(this.rangeMin.min);

    const max =
      webvista.toNumber(this.rangeMax.value);

    value =
      webvista.clampNumber(value, min, max);

    this.rangeMin.value =
      String(value);

    event.currentTarget.value =
      String(value);

    this.setProgressMin();
    this.scheduleEmit();
  }

  /**
   * 最大输入变化
   * @param {Event} event
   */
  onInputMaxChange(event) {
    let value =
      webvista.toNumber(event.currentTarget.value);

    const min =
      webvista.toNumber(this.rangeMin.value);

    const max =
      webvista.toNumber(this.rangeMax.max);

    value =
      webvista.clampNumber(value, min, max);

    this.rangeMax.value =
      String(value);

    event.currentTarget.value =
      String(value);

    this.setProgressMax();
    this.scheduleEmit();
  }

  /**
   * 调整最小值滑块
   */
  setProgressMin() {
    const min =
      webvista.toNumber(this.rangeMin.min);

    const max =
      webvista.toNumber(
        this.rangeMin.max,
        min,
      );

    const span = max - min;

    const cur =
      webvista.toNumber(
        this.rangeMin.value,
        min,
      );

    this.style.setProperty(
      "--start",
      `${((cur - min) * 100) / span}%`,
    );
  }

  /**
   * 调整最大值滑块
   */
  setProgressMax() {
    const min =
      webvista.toNumber(this.rangeMin.min);

    const max =
      webvista.toNumber(
        this.rangeMin.max,
        min,
      );

    const span = max - min;

    const cur =
      webvista.toNumber(
        this.rangeMax.value,
        max,
      );

    this.style.setProperty(
      "--end",
      `${((cur - min) * 100) / span}%`,
    );
  }
}

customElements.define(
  "price-range",
  PriceRange,
);


class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector("a");

    if (!facetLink) return;

    facetLink.setAttribute(
      "role",
      "button",
    );

    facetLink.addEventListener(
      "click",
      this.closeFilter.bind(this),
    );

    facetLink.addEventListener(
      "keyup",
      (event) => {
        event.preventDefault();

        if (
          event.code &&
          event.code.toUpperCase() === "SPACE"
        ) {
          this.closeFilter(event);
        }
      },
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest("facet-filters-form") ||
      document.querySelector(
        "facet-filters-form",
      );

    if (!form) return;

    form.onActiveFilterClick(event);
  }
}

customElements.define(
  "facet-remove",
  FacetRemove,
);